import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PatientsListPage from './pages/patients/PatientsListPage';
import PatientDetailPage from './pages/patients/PatientDetailPage';
import PatientNewPage from './pages/patients/PatientNewPage';
import ImportPage from './pages/import/ImportPage';
import HepatoFormNewPage from './pages/hepato/HepatoFormNewPage';
import HepatoFormDetailPage from './pages/hepato/HepatoFormDetailPage';
import DermatoWizard from './pages/dermato/DermatoWizard';
import DermatoTimelinePage from './pages/dermato/DermatoTimelinePage';
import HepatoWizard from './pages/hepato/HepatoWizard';
import UsersListPage from './pages/users/UsersListPage';
import TestDataCleanupPage from './pages/admin/TestDataCleanupPage';
import TasksPage from './pages/tasks/TasksPage';
import OperatorProfilePage from './pages/profile/OperatorProfilePage';
import ErrorBoundary from './components/common/ErrorBoundary';
import { canUseDermato, canUseHepato } from './utils/flowAccess';
import { features } from './config/features';
import BatchImportPage from './pages/registry-import/BatchImportPage';
import ItemReviewPage from './pages/registry-import/ItemReviewPage';
import RegistryReportsPage from './pages/registry-reports/RegistryReportsPage';
import HepatoWorkspace from './pages/patients/hepato-workspace/HepatoWorkspace';
import DermatoWorkspace from './pages/patients/dermato-workspace/DermatoWorkspace';

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const isAdmin = user?.role?.name === 'ADMINISTRATOR';
  const roleName = user?.role?.name;

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />

        <Route element={isAuthenticated ? <MainLayout /> : <Navigate to="/login" />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/patients" element={<PatientsListPage />} />
          <Route path="/import" element={features.clinicalWorkflow ? <ImportPage /> : <Navigate to="/registry-import" replace />} />
          <Route path="/registry-import" element={<BatchImportPage />} />
          <Route path="/registry-import/items/:id" element={<ItemReviewPage />} />
          <Route path="/registry-reports" element={<RegistryReportsPage />} />
          <Route path="/profile" element={<OperatorProfilePage />} />
          <Route path="/patients/new" element={<PatientNewPage />} />
          <Route path="/patients/:id" element={<PatientDetailPage />} />
          {/*
            Spațiul de lucru hepato (registrul importat, pe cele 4 faze ale fluxului
            medical). Segmentele statice „faza-N” au prioritate față de ruta legacy
            „/patients/:id/hepato/:hepatoId” de mai jos. Nu e condiționat de flag.
          */}
          <Route path="/patients/:id/hepato" element={<Navigate to="faza-1" replace />} />
          <Route path="/patients/:id/hepato/faza-1" element={<HepatoWorkspace phase={1} />} />
          <Route path="/patients/:id/hepato/faza-2" element={<HepatoWorkspace phase={2} />} />
          <Route path="/patients/:id/hepato/faza-3" element={<HepatoWorkspace phase={3} />} />
          <Route path="/patients/:id/hepato/faza-4" element={<HepatoWorkspace phase={4} />} />
          {/* Spațiul de lucru dermato (registrul importat, pe faze) — fără feature flag */}
          <Route path="/patients/:id/dermato" element={<Navigate to="faza-1" replace />} />
          <Route path="/patients/:id/dermato/faza-1" element={<DermatoWorkspace phase={1} />} />
          <Route path="/patients/:id/dermato/faza-2" element={<DermatoWorkspace phase={2} />} />
          <Route path="/patients/:id/dermato/faza-3" element={<DermatoWorkspace phase={3} />} />
          <Route path="/patients/:id/dermato/faza-4" element={<DermatoWorkspace phase={4} />} />
          {/*
            Faza 2 — rutele fluxului clinic (wizard + cronologie + sarcini) se
            înregistrează DOAR cu feature flag-ul activ. Cu flag-ul inactiv ele
            cad pe ruta `*` de mai jos (redirect către dashboard/login).
            Codul paginilor rămâne în repo; doar rutele sunt condiționate.
          */}
          {features.clinicalWorkflow && (
            <>
              <Route path="/tasks" element={<TasksPage />} />
              <Route
                path="/patients/:id/dermato-wizard"
                element={canUseDermato(roleName) ? <DermatoWizard /> : <Navigate to="/patients" />}
              />
              <Route
                path="/patients/:id/dermato-wizard/:step"
                element={canUseDermato(roleName) ? <DermatoWizard /> : <Navigate to="/patients" />}
              />
              <Route
                path="/patients/:id/hepato-wizard"
                element={canUseHepato(roleName) ? <HepatoWizard /> : <Navigate to="/patients" />}
              />
              <Route
                path="/patients/:id/hepato-wizard/:step"
                element={canUseHepato(roleName) ? <HepatoWizard /> : <Navigate to="/patients" />}
              />
              {/*
                Cronologia formularului (ecranul „Timeline" din registru). Aici devine
                vizibil și descărcabil raportul Fotofinder încărcat în wizard.
              */}
              <Route
                path="/dermato/forms/:formId/timeline"
                element={canUseDermato(roleName) ? <DermatoTimelinePage /> : <Navigate to="/patients" />}
              />
            </>
          )}
          <Route
            path="/patients/:id/hepato/new"
            element={canUseHepato(roleName) ? <HepatoFormNewPage /> : <Navigate to="/patients" />}
          />
          <Route
            path="/patients/:id/hepato/:hepatoId"
            element={canUseHepato(roleName) ? <HepatoFormDetailPage /> : <Navigate to="/patients" />}
          />

          {/* Admin Only Routes */}
          {isAdmin && (
            <>
              <Route path="/users" element={<UsersListPage />} />
              <Route path="/admin/test-data" element={<TestDataCleanupPage />} />
            </>
          )}
        </Route>

        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
